import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { clsx } from "clsx";

import { AccountType, AssignmentEntityType } from "@/api/rest/models/account";
import { getAccountAssignments, unassignAccountFromEntity } from "@/api/rest/services/accountService";

import ConfirmationDialog from "./ConfirmationDialog";
import ItemsTable, { Item } from "./ItemsTable";
import UserManagementForm from "./UserManagementForm";
import Spinner from "./Spinner";

interface UserManagementProps {
  entityId: number;
  entityType: AssignmentEntityType;
  onUpdate?: () => void | Promise<void>;
  compact?: boolean;
  addOnly?: boolean;
}

export default function UserManagement({
  entityId,
  entityType,
  onUpdate,
  compact = false,
  addOnly = false,
}: UserManagementProps) {
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [unassigningId, setUnassigningId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState<boolean>(addOnly);

  const { t } = useTranslation();
  const translationKeyPrefix = useMemo(() => {
    if (entityType === AssignmentEntityType.BUILDING) {
      return "buildingSettings";
    }
    return "settings";
  }, [entityType]);

  const {
    data: accountAssignments,
    // isLoading: isLoadingAssignments,
    isFetching: isLoadingAssignments,
    refetch: refetchAssignments,
  } = useQuery({
    queryKey: ["accountAssignments", entityType, entityId],
    queryFn: () => getAccountAssignments(entityType, entityId),
    enabled: Boolean(entityId) && !addOnly,
    staleTime: 1000 * 60 * 5,
  });

  const items = useMemo<Item[]>(
    () =>
      accountAssignments?.result?.assignments?.map((account) => {
        if (account.type === AccountType.INDIVIDUAL) {
          return {
            id: account.id,
            primaryText: account.name,
            secondaryText: account.users?.[0]?.email ?? "Unknown user",
            type: "user" as const,
          };
        }
        return {
          id: account.id,
          primaryText: account.name,
          secondaryText: account.registryCode ?? "Unknown company",
          type: "building" as const,
          subItems: account.users.map((user) => ({
            id: user.id,
            primaryText: user.name,
            secondaryText: user.email,
            type: "user" as const,
          })),
        };
      }) ?? [],
    [accountAssignments],
  );

  const handleUnassignRequest = (itemId: number) => {
    setUnassigningId(itemId);
    setIsConfirmationOpen(true);
  };

  const handleConfirmUnassign = async () => {
    if (unassigningId !== null) {
      await unassignAccountFromEntity(entityType, entityId, {
        accountId: unassigningId,
      });
      await refetchAssignments();
      await onUpdate?.();
      handleCloseUnassign();
    }
  };

  const handleCloseUnassign = () => {
    setIsConfirmationOpen(false);
    setUnassigningId(null);
  };

  return (
    <>
      <div className={clsx("flex flex-col", compact ? "m-0 gap-3" : "m-2")}>
        {!addOnly && (
          <div className="flex flex-col relative gap-2">
            <span className={compact ? "text-sm font-semibold text-gray-900" : "text-xl font-bold"}>
              {t(`${translationKeyPrefix}.accesses`)}
            </span>
            <div className={`pr-2`}>
              {isLoadingAssignments ? (
                <div className="flex justify-center items-center h-full">
                  <Spinner className={"w-6 h-6"} />
                </div>
              ) : (
                <ItemsTable
                  items={items}
                  showAdd={!showForm}
                  onAdd={() => setShowForm(true)}
                  onRemove={handleUnassignRequest}
                  translationKeyPrefix={translationKeyPrefix}
                />
              )}
            </div>
          </div>
        )}
        {(showForm || addOnly) && (
          <UserManagementForm
            entityId={entityId}
            entityType={entityType}
            translationKeyPrefix={translationKeyPrefix}
            compact={compact}
            showTopBorder={!addOnly}
            onUpdate={async () => {
              if (!addOnly) {
                await refetchAssignments();
              }
              await onUpdate?.();
              if (!addOnly) {
                setShowForm(false);
              }
            }}
          />
        )}
      </div>

      {!addOnly && (
        <ConfirmationDialog
          isOpen={isConfirmationOpen}
          onConfirm={handleConfirmUnassign}
          onCancel={handleCloseUnassign}
          title={t("settings.confirmUnassignTitle")}
          message={t("settings.confirmUnassignMessage", {
            name: items.find((item) => item.id === unassigningId)?.primaryText,
          })}
          confirmText={t("settings.confirmUnassign")}
          cancelText={t("common.cancel")}
        />
      )}
    </>
  );
}
